import { Intent } from "@blueprintjs/core";
import { JitsiConference, JitsiParticipant } from "lib/lib-jitsi-meet";
import { action, reaction } from "mobx";
import { ChatRequestManager } from ".";
import { DataMessage } from "../../chat";
import { ChatRequestStatus, ChatRoomState, getDefaultChatUIState, getToaster, RoomState } from "../../common";
import AbstractDataComponent from "./AbstractDataComponent";

export interface ChatRequestManagerProps {
    room: JitsiConference;
    participant: JitsiParticipant;
    roomState: RoomState;
    privateChats: ChatRoomState["private"];
}

export default class extends AbstractDataComponent<ChatRequestManagerProps> {
    @action
    public static handleAcceptChatRequest = (userId: string, room: JitsiConference, roomState: RoomState) => {
        const dataMessage: DataMessage = { kind: "chatAccept" };
        room.sendMessage(dataMessage, userId);
        roomState.privateChatRequests.set(userId, "outgoingAccept");
    };

    private get videoLayout() {
        return this.props.roomState.videoLayout.find(({ userId }) => userId === this.userId)!;
    }

    private get userId() {
        return this.props.participant.getId();
    }

    private toastId?: string;

    public componentDidMount() {
        reaction(() => this.props.roomState.privateChatRequests.get(this.userId), this.handleRequestStatusChange);
    }

    @action
    private handleRequestStatusChange = (status: ChatRequestStatus | undefined) => {
        const { participant, privateChats } = this.props;

        // if chat request has been accepted, initialize the UI state
        if ((status === "incomingAccept" || status === "outgoingAccept") && !privateChats.has(this.userId)) {
            const newChat = getDefaultChatUIState();

            // if we have an incoming request and the dropdown is not open, show
            // a notification
            if (status === "incomingAccept" && !this.videoLayout.isDropdownOpen) {
                this.toastId = getToaster().show({
                    message: `${participant.getDisplayName()} accepted your chat request`,
                    intent: Intent.SUCCESS,
                    action: {
                        text: "Open private chat",
                        onClick: this.handleOpenPrivateChat,
                    },
                    onDismiss: this.handleNotificationDismiss,
                });
            }

            privateChats.set(this.userId, newChat);
        } else if (status === "incomingRequest") {
            this.toastId = getToaster().show({
                message: `${participant.getDisplayName()} sent you a private chat request`,
                intent: Intent.PRIMARY,
                action: {
                    text: "Accept request",
                    onClick: this.handleAcceptChatRequest,
                },
                timeout: 10000,
                onDismiss: this.handleNotificationDismiss,
            });
        }
    };

    private handleAcceptChatRequest = () => {
        ChatRequestManager.handleAcceptChatRequest(this.userId, this.props.room, this.props.roomState);
        this.videoLayout.isDropdownOpen = true;
    };

    @action
    private handleOpenPrivateChat = () => {
        this.videoLayout.isDropdownOpen = true;
        this.handleNotificationDismiss();
    };

    @action
    private handleNotificationDismiss = () => {
        if (this.toastId !== undefined) {
            getToaster().dismiss(this.toastId);
            this.toastId = undefined;
        }
    };
}
